/** Whose incoming-call screen plays the tone. */
export type RingtoneRole = 'user' | 'listener';

/** GET /admin/ringtones — one entry per role, `custom: false` = built-in tone. */
export interface AdminRingtone {
  role: RingtoneRole;
  custom: boolean;
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  version: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export const RINGTONE_MAX_BYTES = 1024 * 1024;
export const RINGTONE_ACCEPT = '.mp3,.wav,.ogg,.m4a,audio/mpeg,audio/wav,audio/ogg,audio/mp4';

export const ROLE_COPY: Record<RingtoneRole, { title: string; who: string; builtIn: string; builtInFile: string }> = {
  listener: {
    title: 'Listener ringtone',
    who: 'Plays on a listener’s phone when a call comes in — with the app open, in the background, or on the lock screen.',
    builtIn: 'Buddy Bounce (built-in)',
    builtInFile: '/ringtones/listener_incoming_call.wav',
  },
  user: {
    title: 'Normal user ringtone',
    who: 'Plays on a user’s phone while they wait for a buddy to answer (softer), and on the automatic call offer.',
    builtIn: 'Warm Welcome (built-in)',
    builtInFile: '/ringtones/user_incoming_call.wav',
  },
};

/**
 * Why a picked file can't be uploaded, or null. The server checks the bytes
 * again; this only saves a pointless upload.
 */
export function ringtoneFileProblem(file: { name: string; size: number }): string | null {
  if (file.size === 0) return 'That file is empty.';
  if (file.size > RINGTONE_MAX_BYTES) return `The ringtone must be 1 MB or smaller (this file is ${formatBytes(file.size)}).`;
  if (!/\.(mp3|wav|ogg|m4a)$/i.test(file.name)) return 'Choose an MP3, WAV, OGG or M4A audio file.';
  return null;
}

export function formatBytes(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
